import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentMarket, MARKET_FUTURES } from '../../utils/MarketStorage';
import { getCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';
import { getStrategyTemplates, applyAutoTemplate, getStrategies } from '../../services/StrategiesService';
import { cleanupData } from '../../services/SettingsService';
import { clearAllLogs } from '../../services/LogsService';
import { resetCache } from '../../services/BeholderService';

// Template recomendado por mercado (modo automático = o bot escolhe os ativos).
const RECOMMENDED = { spot: 'AI_FULL', futures: 'FUTURES_SHORT' };

const PRESETS = [
    { key: 'CONSERVATIVE', icon: '🛡️', label: 'Conservador', hint: 'Menos risco, posições menores.' },
    { key: 'MODERATE', icon: '⚖️', label: 'Moderado', hint: 'Equilíbrio (recomendado).' },
    { key: 'AGGRESSIVE', icon: '🚀', label: 'Agressivo', hint: 'Mais exposição, mais risco.' },
];

/**
 * Assistente guiado que cria a PRIMEIRA estratégia recomendada do mercado atual (spot → AI Full;
 * futuros → Short Direcional), no modo automático, com um preset de risco. Reusa o endpoint de
 * aplicar template (mesma máquina do TemplateGallery). Ciente de mercado + ambiente. Idempotente:
 * detecta se já existe estratégia do template e oferece substituir.
 */
function StrategyWizard() {
    const market = getCurrentMarket();
    const environment = getCurrentEnvironment();
    const templateId = RECOMMENDED[market] || RECOMMENDED.spot;
    const isFutures = market === MARKET_FUTURES;
    const isTest = environment === 'test';

    const [show, setShow] = useState(false);
    const [step, setStep] = useState(0);
    const [template, setTemplate] = useState(null);
    const [preset, setPreset] = useState('MODERATE');
    const [existing, setExisting] = useState(false); // já há estratégia desse mercado?
    // Limpeza pré-recriação: SÓ em teste com estratégia já existente. Zera o histórico de teste
    // (ordens/sinais), logs e cache antes de recriar — pra os relatórios da nova estratégia
    // nascerem limpos. NUNCA em produção (conta quente). Reusa o "Reiniciar testes" do CleanupPanel.
    const [cleanup, setCleanup] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const offerCleanup = isTest && existing;

    useEffect(() => {
        if (!show) return;
        getStrategyTemplates()
            .then(list => setTemplate((list || []).find(t => t.id === templateId) || null))
            .catch(() => setTemplate(null));
        getStrategies('', 1, 100, environment, market)
            .then(res => setExisting(((res && res.rows) || []).length > 0))
            .catch(() => setExisting(false));
    }, [show, templateId, environment, market]);

    function open() { setShow(true); setStep(0); setError(''); setDone(false); setPreset('MODERATE'); }
    function close() { setShow(false); }

    async function apply(replace) {
        setBusy(true); setError('');
        try {
            // Limpeza pré-recriação (só teste + opção marcada): zera ordens/sinais de teste, logs e
            // cache antes de recriar. Em produção offerCleanup é sempre false → nunca roda.
            if (offerCleanup && cleanup) {
                await cleanupData(['orders', 'aiSignals'], 'test', '');
                await clearAllLogs().catch(() => {});
                await resetCache().catch(() => {});
            }
            const config = (template && template.presets && template.presets[preset]) || {};
            await applyAutoTemplate(templateId, 'USDT', config, environment, !!replace);
            setDone(true); setStep(2);
        } catch (err) {
            const msg = (err && err.response && err.response.data && (err.response.data.message || err.response.data)) || (err && err.message) || 'Erro ao criar a estratégia.';
            setError(typeof msg === 'string' ? msg : 'Erro ao criar a estratégia.');
        } finally { setBusy(false); }
    }

    const envLabel = environment === 'test' ? 'Teste' : 'Produção';
    const presetVals = template && template.presets && template.presets[preset];

    return (
        <>
            <button type='button' className={`btn btn-sm ${isFutures ? 'btn-warning' : 'btn-primary'}`} onClick={open}>
                ✨ Criar estratégia recomendada
            </button>

            {show && (
                <>
                    <div className='modal fade show' style={{ display: 'block' }} tabIndex='-1'>
                        <div className='modal-dialog modal-dialog-centered'>
                            <div className='modal-content'>
                                <div className='modal-header'>
                                    <h5 className='modal-title'>
                                        Assistente de estratégia · {isFutures ? '⚡ Futuros' : '🪙 Spot'}
                                        <span className='text-muted ms-2' style={{ fontSize: '0.7em' }}>({envLabel})</span>
                                    </h5>
                                    <button type='button' className='btn-close' onClick={close}></button>
                                </div>
                                <div className='modal-body'>
                                    {step === 0 && (
                                        <>
                                            <p className='mb-2'>
                                                Vou criar a estratégia recomendada para <strong>{isFutures ? 'Futuros' : 'Spot'}</strong>:{' '}
                                                <strong>{template ? template.name : (isFutures ? 'Short Direcional' : 'Setup AI Full')}</strong>, no modo
                                                automático (o bot escolhe os ativos), no ambiente <strong>{envLabel}</strong>.
                                            </p>
                                            {template && <p className='text-muted small mb-2'>{template.description}</p>}
                                            {existing && (
                                                <div className='alert alert-warning py-2 small mb-0'>
                                                    Já existe estratégia neste mercado/ambiente. Você poderá <strong>substituir</strong> a do mesmo nome no final.
                                                </div>
                                            )}
                                            {offerCleanup && (
                                                <div className='form-check mt-3 mb-0'>
                                                    <input className='form-check-input' type='checkbox' id='wizardCleanup'
                                                        checked={cleanup} onChange={e => setCleanup(e.target.checked)} />
                                                    <label className='form-check-label small' htmlFor='wizardCleanup'>
                                                        <strong>Limpar histórico de teste antes de criar</strong> (recomendado) — zera ordens e sinais de IA de teste, logs e cache,
                                                        pra os relatórios da nova estratégia nascerem limpos. <span className='text-muted'>Mantém as estratégias; só vale no ambiente de Teste.</span>
                                                    </label>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {step === 1 && (
                                        <>
                                            <p className='mb-2'>Escolha o perfil de risco:</p>
                                            <div className='d-flex flex-column gap-2'>
                                                {PRESETS.map(p => (
                                                    <button key={p.key} type='button'
                                                        className={`btn text-start ${preset === p.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                        onClick={() => setPreset(p.key)}>
                                                        <strong>{p.icon} {p.label}</strong> <span className={preset === p.key ? '' : 'text-muted'} style={{ fontSize: '0.85em' }}>— {p.hint}</span>
                                                    </button>
                                                ))}
                                            </div>
                                            {presetVals && (
                                                <p className='text-muted small mt-2 mb-0'>
                                                    Valores: {Object.entries(presetVals).map(([k, v]) => `${k}=${v}`).join(' · ')}
                                                </p>
                                            )}
                                            {offerCleanup && cleanup && (
                                                <div className='alert alert-info py-2 small mt-2 mb-0'>
                                                    🧹 Antes de criar, vou limpar o histórico de teste (ordens/sinais), logs e cache.
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {step === 2 && done && (
                                        <div className='text-center py-2'>
                                            <div style={{ fontSize: 40 }}>✅</div>
                                            <p className='mb-1'>Estratégia criada no ambiente <strong>{envLabel}</strong>!</p>
                                            <p className='text-muted small'>Ela nasce <strong>parada</strong>. Ative em Estratégias quando quiser começar.</p>
                                        </div>
                                    )}

                                    {error && <div className='alert alert-danger py-2 small mt-3 mb-0'>{error}</div>}
                                </div>
                                <div className='modal-footer'>
                                    {step === 0 && (<>
                                        <button className='btn btn-light' onClick={close}>Cancelar</button>
                                        <button className='btn btn-primary' onClick={() => setStep(1)}>Continuar</button>
                                    </>)}
                                    {step === 1 && (<>
                                        <button className='btn btn-light' onClick={() => setStep(0)} disabled={busy}>Voltar</button>
                                        {existing
                                            ? <button className='btn btn-warning' onClick={() => apply(true)} disabled={busy}>{busy ? (offerCleanup && cleanup ? 'Limpando e criando…' : 'Criando…') : 'Substituir e criar'}</button>
                                            : <button className='btn btn-success' onClick={() => apply(false)} disabled={busy}>{busy ? 'Criando…' : 'Criar estratégia'}</button>}
                                    </>)}
                                    {step === 2 && (<>
                                        <Link to='/strategies' className='btn btn-primary' onClick={close}>Ver em Estratégias</Link>
                                        <button className='btn btn-light' onClick={close}>Fechar</button>
                                    </>)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='modal-backdrop fade show'></div>
                </>
            )}
        </>
    );
}

export default StrategyWizard;
